<template>
  <article
    class="category-group"
    :class="{ 'category-group--selected': selectedCount > 0 }"
  >
    <div class="category-group__header">
      <v-checkbox
        :input-value="selectedCount === group.categoryCount"
        :indeterminate="selectedCount > 0 && selectedCount < group.categoryCount"
        :aria-label="'Sélectionner toutes les catégories : ' + group.name"
        :disabled="disabled"
        class="ma-0 pa-0 category-group__checkbox"
        hide-details
        @change="toggleAll"
      />
      <div class="category-group__content">
        <div class="category-group__heading">
          <span class="font-weight-medium">{{ group.name }}</span>
          <v-chip
            v-for="status in group.statuses"
            :key="status"
            x-small
            outlined
            :color="statusColor(status)"
          >
            {{ statusLabel(status) }}
          </v-chip>
        </div>
        <div class="text-caption mt-1">
          {{ group.categoryCount }} catégorie(s) · {{ selectedCount }}/{{ group.categoryCount }} sélectionnée(s)
        </div>
        <div class="text-caption mt-1">
          {{ group.offerCount }} offre(s) · {{ assignmentLabel }}
        </div>
        <p
          v-if="elsewhereCount"
          class="text-caption warning--text mt-2 mb-1"
        >
          {{ elsewhereCount }} catégorie(s) déjà rattachée(s) ailleurs.
          Les sélectionner changera leur rattachement à l'enregistrement.
        </p>
        <p
          v-if="group.periodsVary"
          class="text-caption mt-2 mb-0"
        >
          <strong>Périodes variables</strong> : consultez le détail des catégories.
        </p>
        <template v-else-if="group.periods.length">
          <div
            v-if="group.categoryCount > 1"
            class="text-caption mt-2"
          >
            Périodes communes
          </div>
          <ul class="category-group__periods text-caption mt-1">
            <li
              v-for="period in group.periods"
              :key="period.start + '/' + period.end"
            >
              Du {{ formatDate(period.start) }} au {{ formatDate(period.end) }}
            </li>
          </ul>
          <p
            v-if="group.periodCount > group.periods.length"
            class="text-caption mb-0"
          >
            Aperçu : {{ group.periods.length }} périodes sur {{ group.periodCount }}.
          </p>
        </template>
        <p
          v-else
          class="text-caption mt-2 mb-0"
        >
          Aucune période valide enregistrée.
        </p>
        <v-btn
          class="mt-2 px-0"
          text
          small
          color="primary"
          :aria-expanded="expanded ? 'true' : 'false'"
          :aria-controls="'category-group-' + group.id"
          @click="expanded = !expanded"
        >
          {{ expanded ? 'Masquer le détail' : 'Voir les catégories (' + group.categoryCount + ')' }}
        </v-btn>
      </div>
    </div>
    <div
      v-if="expanded"
      :id="'category-group-' + group.id"
      class="category-group__members"
    >
      <p class="text-caption mb-2">
        Catégories distinctes portant le même libellé. Vous pouvez les associer ensemble ou individuellement.
      </p>
      <div
        v-for="category in visibleCategories"
        :key="category.id"
        class="category-group__member"
      >
        <v-checkbox
          :input-value="selectedIds.includes(category.id)"
          :aria-label="'Associer ' + category.name + ' : ' + campingLabel(category) + ' (' + (category.key || category.id) + ')'"
          :disabled="disabled"
          class="ma-0 pa-0 category-group__checkbox"
          hide-details
          @change="toggleCategory(category.id, $event)"
        />
        <div class="category-group__content">
          <div class="font-weight-medium text-body-2">
            {{ campingLabel(category) }}
          </div>
          <details
            v-if="category.campings.length > 3"
            class="text-caption mt-1"
          >
            <summary>Voir tous les campings</summary>
            <ul>
              <li
                v-for="camping in category.campings"
                :key="camping.id"
              >
                {{ camping.name }} · SH {{ camping.sourceId || '—' }}
              </li>
            </ul>
          </details>
          <div class="text-caption mt-1">
            {{ statusLabel(category.status) }} · {{ category.offerCount }} offre(s)
          </div>
          <div class="text-caption mt-1">
            {{ category.superCategoryId ? 'Super-catégorie : ' + (category.superCategoryName || 'Sans libellé') : 'Sans super-catégorie' }}
            {{ category.superCategoryId && category.superCategoryId === superCategoryId ? '(actuelle)' : '' }}
          </div>
          <p
            v-if="category.superCategoryId && category.superCategoryId !== superCategoryId && selectedIds.includes(category.id)"
            class="text-caption warning--text mt-1 mb-0"
          >
            Sera déplacée vers cette super-catégorie à l'enregistrement.
          </p>
          <template v-if="group.periodsVary">
            <ul
              v-if="category.periods.length"
              class="category-group__periods text-caption mt-2"
            >
              <li
                v-for="period in category.periods"
                :key="period.start + '/' + period.end"
              >
                Du {{ formatDate(period.start) }} au {{ formatDate(period.end) }}
              </li>
            </ul>
            <p
              v-else
              class="text-caption mt-2 mb-0"
            >
              Aucune période valide enregistrée.
            </p>
            <p
              v-if="category.periodCount > category.periods.length"
              class="text-caption mb-0"
            >
              Aperçu : {{ category.periods.length }} périodes sur {{ category.periodCount }}.
            </p>
          </template>
          <details class="text-caption mt-2">
            <summary>Identifiants techniques</summary>
            <div>Clé d'import : {{ category.key || 'Non renseignée' }}</div>
            <div>Catégorie : {{ category.id }}</div>
          </details>
        </div>
      </div>
      <v-pagination
        v-if="memberPageCount > 1"
        :value="currentMemberPage"
        :length="memberPageCount"
        :total-visible="5"
        :disabled="disabled"
        class="mt-2"
        @input="memberPage = $event"
      />
    </div>
  </article>
</template>

<script>
export default {
  props: {
    group: { type: Object, required: true },
    selectedIds: { type: Array, default: () => [] },
    superCategoryId: { type: String, default: null },
    disabled: { type: Boolean, default: false }
  },
  data () {
    return { expanded: false, memberPage: 1, memberLimit: 5 }
  },
  computed: {
    selectedCount () {
      const selected = new Set(this.selectedIds)

      return this.group.categories.filter(category => selected.has(category.id)).length
    },
    elsewhereCount () {
      return this.group.categories.filter(category => category.superCategoryId && category.superCategoryId !== this.superCategoryId).length
    },
    assignmentLabel () {
      const assignments = [...new Set(this.group.categories.map(category => category.superCategoryId))]
      if (assignments.length > 1) return 'Rattachements différents'
      if (!assignments[0]) return 'Sans super-catégorie'
      if (assignments[0] === this.superCategoryId) return 'Rattachées à cette super-catégorie'

      return 'Super-catégorie : ' + (this.group.categories[0].superCategoryName || 'Sans libellé')
    },
    memberPageCount () {
      return Math.max(1, Math.ceil(this.group.categories.length / this.memberLimit))
    },
    currentMemberPage () {
      return Math.min(this.memberPage, this.memberPageCount)
    },
    visibleCategories () {
      const start = (this.currentMemberPage - 1) * this.memberLimit

      return this.group.categories.slice(start, start + this.memberLimit)
    }
  },
  methods: {
    toggleAll (checked) {
      if (this.disabled) return
      this.$emit('change', { ids: this.group.categories.map(category => category.id), checked })
    },
    toggleCategory (id, checked) {
      if (this.disabled) return
      this.$emit('change', { ids: [id], checked })
    },
    campingLabel (category) {
      if (!category.campings.length) return 'Aucun camping associé'
      const names = category.campings.slice(0, 3).map(camping => camping.name + (camping.sourceId ? ' (SH ' + camping.sourceId + ')' : ''))

      return names.join(', ') + (category.campings.length > 3 ? ' + ' + (category.campings.length - 3) + ' autres' : '')
    },
    statusLabel (status) {
      return { current: 'En cours', upcoming: 'À venir', past: 'Terminée', empty: 'Sans offre', undated: 'Sans période valide' }[status] || status
    },
    statusColor (status) {
      return { current: 'success', upcoming: 'primary', past: 'secondary', empty: 'secondary', undated: 'warning' }[status]
    },
    formatDate (value) {
      if (!value) return '—'
      const [year, month, day] = value.split('-')

      return `${day}/${month}/${year}`
    }
  }
}
</script>

<style scoped>
.category-group {
  border-bottom: 1px solid rgba(94, 86, 105, 0.12);
  padding: 16px 8px;
}
.category-group--selected {
  background: rgba(94, 86, 105, 0.04);
}
.category-group__header,
.category-group__member {
  display: flex;
  gap: 12px;
}
.category-group__checkbox {
  flex: 0 0 auto;
}
.category-group__content {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}
.category-group__heading {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.category-group__periods {
  padding-left: 16px;
}
.category-group__members {
  margin: 12px 0 0 8px;
  padding: 12px 0 0 16px;
  border-left: 2px solid rgba(94, 86, 105, 0.2);
}
.category-group__member {
  padding: 12px 0;
  border-top: 1px solid rgba(94, 86, 105, 0.12);
}
.category-group summary {
  cursor: pointer;
}
</style>
